import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import { AdminApproval } from '../../components/common/AdminApproval';
interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}
export function RoleManagement() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [pendingAction, setPendingAction] = useState<'add' | 'edit' | 'delete' | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
    status: 'active' as 'active' | 'inactive'
  });
  // Sample permissions
  const availablePermissions = ['dashboard_view', 'transactions_view', 'transactions_manage', 'users_view', 'users_manage', 'roles_view', 'roles_manage', 'configurations_view', 'configurations_manage', 'reports_view', 'audit_log_view'];
  // Sample data
  useEffect(() => {
    setRoles([{
      id: 1,
      name: 'Administrator',
      description: 'Full system access',
      permissions: availablePermissions,
      status: 'active',
      createdAt: '2023-01-15',
      updatedAt: '2023-03-20'
    }, {
      id: 2,
      name: 'Manager',
      description: 'Access to manage users and view reports',
      permissions: ['dashboard_view', 'transactions_view', 'users_view', 'users_manage', 'roles_view', 'reports_view', 'audit_log_view'],
      status: 'active',
      createdAt: '2023-01-20',
      updatedAt: '2023-02-10'
    }, {
      id: 3,
      name: 'Operator',
      description: 'Limited access to operational functions',
      permissions: ['dashboard_view', 'transactions_view', 'reports_view'],
      status: 'active',
      createdAt: '2023-02-05',
      updatedAt: '2023-02-05'
    }, {
      id: 4,
      name: 'Auditor',
      description: 'View-only access for audit purposes',
      permissions: ['dashboard_view', 'transactions_view', 'reports_view', 'audit_log_view'],
      status: 'inactive',
      createdAt: '2023-03-10',
      updatedAt: '2023-04-15'
    }]);
  }, []);
  const filteredRoles = roles.filter(role => role.name.toLowerCase().includes(searchTerm.toLowerCase()) || role.description.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAddRole = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
      status: 'active'
    });
    setShowAddForm(true);
  };
  const handleEditRole = (role: Role) => {
    setCurrentRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: [...role.permissions],
      status: role.status
    });
    setShowEditForm(true);
  };
  const handleDeleteRole = (role: Role) => {
    setCurrentRole(role);
    setShowDeleteConfirm(true);
  };
  const handlePermissionChange = (permission: string) => {
    setFormData(prev => {
      if (prev.permissions.includes(permission)) {
        return {
          ...prev,
          permissions: prev.permissions.filter(p => p !== permission)
        };
      } else {
        return {
          ...prev,
          permissions: [...prev.permissions, permission]
        };
      }
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showAddForm) {
      setPendingAction('add');
    } else if (showEditForm) {
      setPendingAction('edit');
    }
    setShowApprovalModal(true);
    setShowAddForm(false);
    setShowEditForm(false);
  };
  const handleApprove = () => {
    if (pendingAction === 'add') {
      const newRole: Role = {
        id: roles.length + 1,
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions,
        status: formData.status,
        createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0]
      };
      setRoles([...roles, newRole]);
    } else if (pendingAction === 'edit' && currentRole) {
      const updatedRoles = roles.map(role => role.id === currentRole.id ? {
        ...role,
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions,
        status: formData.status,
        updatedAt: new Date().toISOString().split('T')[0]
      } : role);
      setRoles(updatedRoles);
    } else if (pendingAction === 'delete' && currentRole) {
      const updatedRoles = roles.filter(role => role.id !== currentRole.id);
      setRoles(updatedRoles);
    }
    setShowApprovalModal(false);
    setPendingAction(null);
    setCurrentRole(null);
    setShowDeleteConfirm(false);
  };
  const handleReject = () => {
    setShowApprovalModal(false);
    setPendingAction(null);
    setShowDeleteConfirm(false);
  };
  const confirmDelete = () => {
    setPendingAction('delete');
    setShowDeleteConfirm(false);
    setShowApprovalModal(true);
  };
  const formatPermissionName = (permission: string) => {
    return permission.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const groupPermissions = () => {
    const groups: {
      [key: string]: string[];
    } = {};
    availablePermissions.forEach(permission => {
      const [module] = permission.split('_');
      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(permission);
    });
    return groups;
  };
  const permissionGroups = groupPermissions();
  return <div className="space-y-6" data-id="element-755">
      <div className="flex justify-between items-center" data-id="element-756">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-757">Role Management</h1>
        <button onClick={handleAddRole} className="flex items-center px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-758">
          <PlusIcon size={16} className="mr-2" data-id="element-759" />
          Add New Role
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4" data-id="element-760">
        <div className="mb-4 relative" data-id="element-761">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-762">
            <SearchIcon size={16} className="text-gray-400" data-id="element-763" />
          </div>
          <input type="text" placeholder="Search roles..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-764" />
        </div>
        <div className="overflow-x-auto" data-id="element-765">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-766">
            <thead className="bg-gray-50" data-id="element-767">
              <tr data-id="element-768">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-769">
                  Role Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-770">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-771">
                  Permissions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-772">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-773">
                  Last Updated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-774">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-775">
              {filteredRoles.map(role => <tr key={role.id} data-id="element-776">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-777">
                    {role.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-778">
                    {role.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-779">
                    <div className="flex flex-wrap gap-1" data-id="element-780">
                      {role.permissions.slice(0, 3).map(permission => <span key={permission} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-781">
                          {formatPermissionName(permission)}
                        </span>)}
                      {role.permissions.length > 3 && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-782">
                          +{role.permissions.length - 3} more
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-783">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${role.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-784">
                      {role.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-785">
                    {role.updatedAt}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-786">
                    <div className="flex space-x-2" data-id="element-787">
                      <button onClick={() => handleEditRole(role)} className="text-blue-600 hover:text-blue-900" data-id="element-788">
                        <EditIcon size={16} data-id="element-789" />
                      </button>
                      <button onClick={() => handleDeleteRole(role)} className="text-red-600 hover:text-red-900" data-id="element-790">
                        <TrashIcon size={16} data-id="element-791" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {filteredRoles.length === 0 && <tr data-id="element-792">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-793">
                    No roles found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add Role Form */}
      {showAddForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-794">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 overflow-y-auto max-h-[90vh]" data-id="element-795">
            <div className="flex justify-between items-center mb-4" data-id="element-796">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-797">
                Add New Role
              </h3>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-798">
                <XCircleIcon size={20} data-id="element-799" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-800">
              <div className="space-y-4" data-id="element-801">
                <div data-id="element-802">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-803">
                    Role Name
                  </label>
                  <input type="text" id="name" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} data-id="element-804" />
                </div>
                <div data-id="element-805">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-806">
                    Description
                  </label>
                  <textarea id="description" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} data-id="element-807"></textarea>
                </div>
                <div data-id="element-808">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-809">
                    Status
                  </label>
                  <div className="flex space-x-4" data-id="element-810">
                    <label className="inline-flex items-center" data-id="element-811">
                      <input type="radio" className="form-radio text-[#F58220] focus:ring-[#F58220]" name="status" checked={formData.status === 'active'} onChange={() => setFormData({
                    ...formData,
                    status: 'active'
                  })} data-id="element-812" />
                      <span className="ml-2" data-id="element-813">Active</span>
                    </label>
                    <label className="inline-flex items-center" data-id="element-814">
                      <input type="radio" className="form-radio text-[#F58220] focus:ring-[#F58220]" name="status" checked={formData.status === 'inactive'} onChange={() => setFormData({
                    ...formData,
                    status: 'inactive'
                  })} data-id="element-815" />
                      <span className="ml-2" data-id="element-816">Inactive</span>
                    </label>
                  </div>
                </div>
                <div data-id="element-817">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-818">
                    Permissions
                  </label>
                  <div className="border border-gray-300 rounded-md p-4 max-h-60 overflow-y-auto" data-id="element-819">
                    {Object.entries(permissionGroups).map(([module, permissions]) => <div key={module} className="mb-4" data-id="element-820">
                          <h4 className="text-sm font-semibold mb-2 capitalize" data-id="element-821">
                            {module}
                          </h4>
                          <div className="grid grid-cols-2 gap-2" data-id="element-822">
                            {permissions.map(permission => <label key={permission} className="inline-flex items-center" data-id="element-823">
                                <input type="checkbox" className="form-checkbox text-[#F58220] focus:ring-[#F58220] rounded" checked={formData.permissions.includes(permission)} onChange={() => handlePermissionChange(permission)} data-id="element-824" />
                                <span className="ml-2 text-sm" data-id="element-825">
                                  {formatPermissionName(permission)}
                                </span>
                              </label>)}
                          </div>
                        </div>)}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-826">
                <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-827">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-828">
                  Save Role
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Edit Role Form */}
      {showEditForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-829">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 overflow-y-auto max-h-[90vh]" data-id="element-830">
            <div className="flex justify-between items-center mb-4" data-id="element-831">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-832">
                Edit Role
              </h3>
              <button onClick={() => setShowEditForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-833">
                <XCircleIcon size={20} data-id="element-834" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-835">
              <div className="space-y-4" data-id="element-836">
                <div data-id="element-837">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-838">
                    Role Name
                  </label>
                  <input type="text" id="name" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} data-id="element-839" />
                </div>
                <div data-id="element-840">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-841">
                    Description
                  </label>
                  <textarea id="description" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} data-id="element-842"></textarea>
                </div>
                <div data-id="element-843">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-844">
                    Status
                  </label>
                  <div className="flex space-x-4" data-id="element-845">
                    <label className="inline-flex items-center" data-id="element-846">
                      <input type="radio" className="form-radio text-[#F58220] focus:ring-[#F58220]" name="status" checked={formData.status === 'active'} onChange={() => setFormData({
                    ...formData,
                    status: 'active'
                  })} data-id="element-847" />
                      <span className="ml-2" data-id="element-848">Active</span>
                    </label>
                    <label className="inline-flex items-center" data-id="element-849">
                      <input type="radio" className="form-radio text-[#F58220] focus:ring-[#F58220]" name="status" checked={formData.status === 'inactive'} onChange={() => setFormData({
                    ...formData,
                    status: 'inactive'
                  })} data-id="element-850" />
                      <span className="ml-2" data-id="element-851">Inactive</span>
                    </label>
                  </div>
                </div>
                <div data-id="element-852">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-853">
                    Permissions
                  </label>
                  <div className="border border-gray-300 rounded-md p-4 max-h-60 overflow-y-auto" data-id="element-854">
                    {Object.entries(permissionGroups).map(([module, permissions]) => <div key={module} className="mb-4" data-id="element-855">
                          <h4 className="text-sm font-semibold mb-2 capitalize" data-id="element-856">
                            {module}
                          </h4>
                          <div className="grid grid-cols-2 gap-2" data-id="element-857">
                            {permissions.map(permission => <label key={permission} className="inline-flex items-center" data-id="element-858">
                                <input type="checkbox" className="form-checkbox text-[#F58220] focus:ring-[#F58220] rounded" checked={formData.permissions.includes(permission)} onChange={() => handlePermissionChange(permission)} data-id="element-859" />
                                <span className="ml-2 text-sm" data-id="element-860">
                                  {formatPermissionName(permission)}
                                </span>
                              </label>)}
                          </div>
                        </div>)}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-861">
                <button type="button" onClick={() => setShowEditForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-862">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-863">
                  Update Role
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Delete Confirmation */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-864">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-865">
            <div className="flex items-center mb-4 text-red-600" data-id="element-866">
              <AlertCircleIcon size={24} className="mr-2" data-id="element-867" />
              <h3 className="text-lg font-semibold" data-id="element-868">Delete Role</h3>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-869">
              Are you sure you want to delete the role "{currentRole?.name}"?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-870">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-871">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-872">
                Delete
              </button>
            </div>
          </div>
        </div>}
      {/* Admin Approval Modal */}
      <AdminApproval isVisible={showApprovalModal} onClose={() => setShowApprovalModal(false)} onApprove={handleApprove} onReject={handleReject} title={pendingAction === 'add' ? 'Approve New Role' : pendingAction === 'edit' ? 'Approve Role Update' : 'Approve Role Deletion'} description={pendingAction === 'add' ? `Please review and approve the creation of the new role "${formData.name}".` : pendingAction === 'edit' ? `Please review and approve the updates to the role "${formData.name}".` : `Please review and approve the deletion of the role "${currentRole?.name}".`} data-id="element-873" />
    </div>;
}